import React, { useState, useEffect, useRef } from 'react';
import {
  Calculator,
  Loader2,
  ArrowRightLeft,
  Building2,
  Wallet,
  CreditCard,
  TrendingUp,
  TrendingDown,
  Plus,
  Search,
  Filter,
  Calendar,
  CheckCircle,
  AlertCircle,
  ArrowRight,
  ChevronDown,
  X,
  Download,
} from 'lucide-react';
import config from '../config/config';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx'; // npm install xlsx

const AccountTransferManager = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('transfer');
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [transferAmount, setTransferAmount] = useState('');
  const [fromAccount, setFromAccount] = useState('');
  const [startDate, setStartDate] = useState('2025-05-01');
  const [termId, setTerm] = useState('all');
  const [filteredOverallRecords, setFilteredOverallRecords] = useState([]);
  const [filteredOverallFeeRecords, setFilteredOverallFeeRecords] = useState([]);
  const [filteredOverallTransportRecords, setFilteredOverallTransportRecords] = useState([]);
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const formattedTomorrow = tomorrow.toISOString().split('T')[0];
  const [endDate, setEndDate] = useState(formattedTomorrow);
  const [toAccount, setToAccount] = useState('');
  const [selectedClass, setSelectedClass] = useState('');
  const [description, setDescription] = useState('');
  const [reference, setReference] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const token = localStorage.getItem('token');
  const [transactions, setTransactions] = useState([]);
  const [overallRecords, setOverallRecords] = useState([]);
  const [overallFeeRecords, setOverallFeeRecords] = useState([]);
  const [overallTransportRecords, setOverallTransportRecords] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [journalEntries, setJournalEntries] = useState([]);
  const [terms, setTerms] = useState([]);

  // Account selection states
  const [fromAccountSearch, setFromAccountSearch] = useState('');
  const [toAccountSearch, setToAccountSearch] = useState('');
  const [showFromDropdown, setShowFromDropdown] = useState(false);
  const [showToDropdown, setShowToDropdown] = useState(false);
  const [selectedFromAccount, setSelectedFromAccount] = useState(null);
  const [selectedToAccount, setSelectedToAccount] = useState(null);

  const fromDropdownRef = useRef(null);
  const toDropdownRef = useRef(null);

  const classGrades = [
    { value: 'PG', label: 'Playgroup (2-3 years)' },
    { value: 'PP1', label: 'Pre-primary 1 (4-5 years)' },
    { value: 'PP2', label: 'Pre-primary 2 (4-5 years)' },
    { value: 'G1', label: 'Grade 1 (6-8 years)' },
    { value: 'G2', label: 'Grade 2 (6-8 years)' },
    { value: 'G3', label: 'Grade 3 (6-8 years)' },
    { value: 'G4', label: 'Grade 4 (9 years)' },
    { value: 'G5', label: 'Grade 5 (9 years)' },
    { value: 'G6', label: 'Grade 6 (11 years)' },
  ];

  // Fetch terms
  const fetchTerms = async () => {
    try {
      const res = await axios.get(`${config.API_URL}/api/pullterms`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true,
      });
      setTerms(res.data.terms || res.data || []);
    } catch (err) {
      console.error('Error fetching terms:', err);
    }
  };

  // Fetch fee reports
  const fetchPettyCash = async (companyId = 1) => {
    setLoading(true);
    try {
      const response = await fetch(
        `${config.API_URL}/api/fetch-fee-reports/${termId}/${startDate}/${endDate}/${selectedClass}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: 'application/json',
          },
        }
      );
      const data = await response.json();

      // Set main records
      setOverallRecords(data.overallRecords || []);
      setOverallFeeRecords(data.overallFeeRecords || []);
      setOverallTransportRecords(data.overallTransportRecords || []);
      setAccounts(data.accounts || []);
      setJournalEntries(data.journals || []);
      handleAccountFilter('Total Overall Fee');
      // Fetch terms
      await fetchTerms();

      // Initialize filtered records
      setFilteredOverallRecords(filteredOverallRecord(''));
      setFilteredOverallFeeRecords(filteredOverallFeeRecord(''));
      setFilteredOverallTransportRecords(filteredOverallTransportRecord(''));

      setLoading(false);
    } catch (err) {
      setLoading(false);
      console.error('Fetch petty cash error:', err);
    }
  };

  useEffect(() => {
    fetchPettyCash();
  }, [token, termId, startDate, endDate, selectedClass]);

  // Sample accounts data (unchanged)
  const [accountz, setAccountz] = useState([
    { id: 'cash', name: 'Cash in Hand', code: '1001', type: 'asset', category: 'current', balance: 5500.00, normalBalance: 'debit' },
    { id: 'checking', name: 'Checking Account', code: '1100', type: 'asset', category: 'current', balance: 45230.00, normalBalance: 'debit' },
    { id: 'savings', name: 'Business Savings', code: '1200', type: 'asset', category: 'current', balance: 125000.00, normalBalance: 'debit' },
    { id: 'petty-cash', name: 'Petty Cash Fund', code: '1050', type: 'asset', category: 'current', balance: 2500.00, normalBalance: 'debit' },
    { id: 'money-market', name: 'Money Market Account', code: '1210', type: 'asset', category: 'current', balance: 75000.00, normalBalance: 'debit' },
    { id: 'accounts-receivable', name: 'Accounts Receivable', code: '1300', type: 'asset', category: 'current', balance: 18750.00, normalBalance: 'debit' },
    { id: 'inventory', name: 'Inventory', code: '1400', type: 'asset', category: 'current', balance: 32000.00, normalBalance: 'debit' },
    { id: 'prepaid-insurance', name: 'Prepaid Insurance', code: '1500', type: 'asset', category: 'current', balance: 3600.00, normalBalance: 'debit' },
    { id: 'prepaid-rent', name: 'Prepaid Rent', code: '1510', type: 'asset', category: 'current', balance: 6000.00, normalBalance: 'debit' },
    { id: 'office-supplies', name: 'Office Supplies', code: '1520', type: 'asset', category: 'current', balance: 1200.00, normalBalance: 'debit' },
    { id: 'equipment', name: 'Office Equipment', code: '1600', type: 'asset', category: 'fixed', balance: 25000.00, normalBalance: 'debit' },
    { id: 'vehicles', name: 'Company Vehicles', code: '1700', type: 'asset', category: 'fixed', balance: 45000.00, normalBalance: 'debit' },
    { id: 'building', name: 'Building', code: '1800', type: 'asset', category: 'fixed', balance: 250000.00, normalBalance: 'debit' },
    { id: 'land', name: 'Land', code: '1900', type: 'asset', category: 'fixed', balance: 100000.00, normalBalance: 'debit' },
    { id: 'accounts-payable', name: 'Accounts Payable', code: '2100', type: 'liability', category: 'current', balance: 12400.00, normalBalance: 'credit' },
    { id: 'wages-payable', name: 'Wages Payable', code: '2200', type: 'liability', category: 'current', balance: 8500.00, normalBalance: 'credit' },
    { id: 'taxes-payable', name: 'Taxes Payable', code: '2300', type: 'liability', category: 'current', balance: 5200.00, normalBalance: 'credit' },
    { id: 'credit-line', name: 'Business Credit Line', code: '2400', type: 'liability', category: 'current', balance: 15000.00, normalBalance: 'credit' },
    { id: 'accrued-expenses', name: 'Accrued Expenses', code: '2500', type: 'liability', category: 'current', balance: 3200.00, normalBalance: 'credit' },
    { id: 'loan-payable', name: 'Bank Loan Payable', code: '2600', type: 'liability', category: 'long-term', balance: 125000.00, normalBalance: 'credit' },
    { id: 'mortgage', name: 'Building Mortgage', code: '2700', type: 'liability', category: 'long-term', balance: 185000.00, normalBalance: 'credit' },
    { id: 'equipment-loan', name: 'Equipment Loan', code: '2800', type: 'liability', category: 'long-term', balance: 18500.00, normalBalance: 'credit' },
    { id: 'capital', name: "Owner's Capital", code: '3100', type: 'equity', category: 'capital', balance: 150000.00, normalBalance: 'credit' },
    { id: 'retained-earnings', name: 'Retained Earnings', code: '3200', type: 'equity', category: 'retained', balance: 85080.00, normalBalance: 'credit' },
    { id: 'drawings', name: "Owner's Drawings", code: '3300', type: 'equity', category: 'drawings', balance: 12000.00, normalBalance: 'debit' },
    { id: 'sales-revenue', name: 'Sales Revenue', code: '4100', type: 'revenue', category: 'operating', balance: 285000.00, normalBalance: 'credit' },
    { id: 'service-revenue', name: 'Service Revenue', code: '4200', type: 'revenue', category: 'operating', balance: 125000.00, normalBalance: 'credit' },
    { id: 'interest-income', name: 'Interest Income', code: '4300', type: 'revenue', category: 'non-operating', balance: 2500.00, normalBalance: 'credit' },
    { id: 'office-expenses', name: 'Office Expenses', code: '5100', type: 'expense', category: 'operating', balance: 12500.00, normalBalance: 'debit' },
    { id: 'rent-expense', name: 'Rent Expense', code: '5200', type: 'expense', category: 'operating', balance: 36000.00, normalBalance: 'debit' },
    { id: 'salary-expense', name: 'Salary Expense', code: '5300', type: 'expense', category: 'operating', balance: 156000.00, normalBalance: 'debit' },
    { id: 'utilities-expense', name: 'Utilities Expense', code: '5400', type: 'expense', category: 'operating', balance: 8400.00, normalBalance: 'debit' },
    { id: 'insurance-expense', name: 'Insurance Expense', code: '5500', type: 'expense', category: 'operating', balance: 14400.00, normalBalance: 'debit' },
    { id: 'maintenance-expense', name: 'Maintenance Expense', code: '5600', type: 'expense', category: 'operating', balance: 6800.00, normalBalance: 'debit' },
    { id: 'marketing-expense', name: 'Marketing Expense', code: '5700', type: 'expense', category: 'operating', balance: 18500.00, normalBalance: 'debit' },
    { id: 'travel-expense', name: 'Travel Expense', code: '5800', type: 'expense', category: 'operating', balance: 9200.00, normalBalance: 'debit' },
    { id: 'depreciation-expense', name: 'Depreciation Expense', code: '5900', type: 'expense', category: 'operating', balance: 15600.00, normalBalance: 'debit' },
    { id: 'interest-expense', name: 'Interest Expense', code: '6100', type: 'expense', category: 'non-operating', balance: 8900.00, normalBalance: 'debit' },
  ]);

  const [journalEntriez, setJournalEntriez] = useState([
    {
      id: 1,
      date: '2024-09-24',
      reference: 'TRF001',
      description: 'Transfer from Checking to Petty Cash',
      entries: [
        { account: 'petty-cash', accountName: 'Petty Cash Fund', debit: 500.00, credit: 0 },
        { account: 'checking', accountName: 'Checking Account', debit: 0, credit: 500.00 },
      ],
      total: 500.00,
      status: 'posted',
    },
    {
      id: 2,
      date: '2024-09-23',
      reference: 'TRF002',
      description: 'Transfer from Savings to Checking for operations',
      entries: [
        { account: 'checking', accountName: 'Checking Account', debit: 10000.00, credit: 0 },
        { account: 'savings', accountName: 'Business Savings', debit: 0, credit: 10000.00 },
      ],
      total: 10000.00,
      status: 'posted',
    },
  ]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (fromDropdownRef.current && !fromDropdownRef.current.contains(event.target)) {
        setShowFromDropdown(false);
      }
      if (toDropdownRef.current && !toDropdownRef.current.contains(event.target)) {
        setShowToDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const generateReference = () => {
    const count = journalEntries.length + 1;
    return `TRF${count.toString().padStart(3, '0')}`;
  };

  const handleTransfer = async () => {
    setShowTransferModal(false);
    fetchPettyCash();
  };

  const getAccountTypeColor = (type) => {
    const colors = {
      asset: 'bg-blue-50 text-blue-700 border-blue-200',
      liability: 'bg-red-50 text-red-700 border-red-200',
      equity: 'bg-purple-50 text-purple-700 border-purple-200',
      revenue: 'bg-green-50 text-green-700 border-green-200',
      expense: 'bg-orange-50 text-orange-700 border-orange-200',
    };
    return colors[type] || 'bg-gray-50 text-gray-700 border-gray-200';
  };

  const getAccountIcon = (type) => {
    const icons = {
      asset: Building2,
      liability: CreditCard,
      equity: TrendingUp,
      revenue: TrendingUp,
      expense: TrendingDown,
    };
    const Icon = icons[type] || Building2;
    return <Icon className="w-4 h-4" />;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES',
    }).format(Math.abs(amount));
  };

  const calculateTotalsByType = () => {
    return accounts.reduce((totals, acc) => {
      totals[acc.type] = (totals[acc.type] || 0) + acc.balance;
      return totals;
    }, {});
  };

  // Filter accounts based on search
  const getFilteredAccounts = (searchTerm, excludeId = null) => {
    return accounts.filter((account) => {
      if (excludeId && account.id === excludeId) return false;
      const searchLower = searchTerm.toLowerCase();
      return (
        account.name.toLowerCase().includes(searchLower) ||
        account.code.toLowerCase().includes(searchLower) ||
        account.type.toLowerCase().includes(searchLower)
      );
    });
  };

  const filteredOverallRecord = (searchTerm, type = null) => {
    return overallRecords.filter((account) => {
      if (type === 'Total Overall Overpayment') {
        setActiveTab('transfer');
        return account.excess > 0;
      }
      if (type === 'Total Overall Balance') {
        setActiveTab('transfer');
        return account.balance > 0;
      }
      if (type === 'Total Overall Paid') {
        setActiveTab('transfer');
        return account.paid > 0;
      }

      const searchLower = searchTerm.toLowerCase();
      return (
        account.studentName.toLowerCase().includes(searchLower) ||
        account.admission.toLowerCase().includes(searchLower)
      );
    });
  };

  const filteredOverallFeeRecord = (searchTerm, type = null) => {
    return overallFeeRecords.filter((account) => {
      if (type === 'School Fee OverPayment') {
        setActiveTab('accounts');
        return account.excess > 0;
      }
      if (type === 'School Fee Balance') {
        setActiveTab('accounts');
        return account.balance > 0;
      }
      if (type === 'School Fee Paid') {
        setActiveTab('accounts');
        return account.paid > 0;
      }
      const searchLower = searchTerm.toLowerCase();
      return (
        account.studentName.toLowerCase().includes(searchLower) ||
        account.admission.toLowerCase().includes(searchLower)
      );
    });
  };

  const filteredOverallTransportRecord = (searchTerm, type = null) => {
    return overallTransportRecords.filter((account) => {
      if (type === 'Transport Fee Overpayment') {
        setActiveTab('transport');
        return account.excess > 0;
      }
      if (type === 'Transport Fee Balance') {
        setActiveTab('transport');
        return account.balance > 0;
      }
      if (type === 'Transport Fee Paid') {
        setActiveTab('transport');
        return account.paid > 0;
      }
      const searchLower = searchTerm.toLowerCase();
      return (
        account.studentName.toLowerCase().includes(searchLower) ||
        account.admission.toLowerCase().includes(searchLower)
      );
    });
  };

  // Group accounts by type
  const groupAccountsByType = (filteredAccounts) => {
    const groups = filteredAccounts.reduce((acc, account) => {
      if (!acc[account.type]) acc[account.type] = [];
      acc[account.type].push(account);
      return acc;
    }, {});

    const typeOrder = ['asset', 'liability', 'equity', 'revenue', 'expense'];
    return typeOrder
      .filter((type) => groups[type])
      .map((type) => ({
        type,
        accounts: groups[type].sort((a, b) => a.code.localeCompare(b.code)),
      }));
  };

  const setSearchTerms = (searchTerm) => {
    setSearchTerm(searchTerm);
    setFilteredOverallRecords(filteredOverallRecord(searchTerm));
  };

  const setSearchTerm2 = (searchTerm) => {
    setSearchTerm(searchTerm);
    setFilteredOverallFeeRecords(filteredOverallFeeRecord(searchTerm));
  };

  const setSearchTerm3 = (searchTerm) => {
    setSearchTerm(searchTerm);
    setFilteredOverallTransportRecords(filteredOverallTransportRecord(searchTerm));
  };

  const handleAccountFilter = (type) => {
    setFilteredOverallRecords(filteredOverallRecord('', type));
    setFilteredOverallFeeRecords(filteredOverallFeeRecord('', type));
    setFilteredOverallTransportRecords(filteredOverallTransportRecord('', type));
  };

  const totals = calculateTotalsByType();

  // Excel Export Function
  const exportToExcel = (data, fileName, sheetName) => {
    if (!data || data.length === 0) {
      alert('No data to export');
      return;
    }

    const exportData = data.map((item) => ({
      Admission: item.admission || '',
      Student: item.studentName || '',
      Class: item.class || '',
      'Total Fee': Number(item.amount || 0),
      'Total Paid': Number(item.paid || 0),
      Balance: Number(item.balance || 0),
      Excess: Number(item.excess || 0),
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);

    // Set column widths
    ws['!cols'] = [
      { wch: 14 },
      { wch: 40 },
      { wch: 16 },
       { wch: 16 },
      { wch: 16 },
      { wch: 16 },
      { wch: 16 },
    ];

    // Style header row
    const range = XLSX.utils.decode_range(ws['!ref']);
    for (let C = range.s.c; C <= range.e.c; ++C) {
      const cellAddress = XLSX.utils.encode_cell({ r: 0, c: C });
      if (!ws[cellAddress]) continue;
      ws[cellAddress].s = {
        font: { bold: true },
        fill: { fgColor: { rgb: 'E5E7EB' } },
        alignment: { horizontal: 'center' },
      };
    }

    // Currency formatting
    for (let R = 1; R <= range.e.r; ++R) {
      ['C', 'D', 'E', 'F'].forEach((col) => {
        const cellAddress = col + (R + 1);
        if (ws[cellAddress]) {
          ws[cellAddress].z = '_(* #,##0_);_(* (#,##0);_(* "-"_);_(@_)';
          ws[cellAddress].s = {
            ...ws[cellAddress].s,
            alignment: { horizontal: 'right' },
          };
        }
      });
    }

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, sheetName);
    XLSX.writeFile(wb, `${fileName}_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  // Account Selector Component (unchanged)
  const AccountSelector = ({
    label,
    searchValue,
    setSearchValue,
    selectedAccount,
    setSelectedAccount,
    showDropdown,
    setShowDropdown,
    dropdownRef,
    excludeAccountId,
    placeholder,
  }) => {
    const filteredAccounts = getFilteredAccounts(searchValue, excludeAccountId);
    const groupedAccounts = groupAccountsByType(filteredAccounts);

    if (loading) {
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
          <div className="flex flex-col items-center gap-3">
            <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
            <p className="text-base text-gray-600 font-medium">Loading fee balances...</p>
          </div>
        </div>
      );
    }

    return (
      <div className="relative" ref={dropdownRef}>
        <label className="block text-sm font-medium text-gray-700 mb-2">{label}</label>
        {selectedAccount ? (
          <div className="w-full p-3 border border-gray-200 rounded-xl bg-gray-50 flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className={`p-2 rounded-lg ${getAccountTypeColor(selectedAccount.type)}`}>
                {getAccountIcon(selectedAccount.type)}
              </div>
              <div>
                <p className="font-medium text-gray-900">{selectedAccount.name}</p>
                <p className="text-sm text-gray-500">
                  {selectedAccount.code} • {formatCurrency(selectedAccount.balance)}
                </p>
              </div>
            </div>
            <button
              onClick={() => {
                setSelectedAccount(null);
                setSearchValue('');
                setShowDropdown(true);
              }}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        ) : (
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              value={searchValue}
              onChange={(e) => {
                setSearchValue(e.target.value);
                setShowDropdown(true);
              }}
              onFocus={() => setShowDropdown(true)}
              placeholder={placeholder}
              className="w-full pl-10 pr-10 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent transition-all duration-200"
            />
            <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          </div>
        )}

        {showDropdown && !selectedAccount && (
          <div className="absolute z-50 w-full cursor-pointer mt-2 bg-white border border-gray-200 rounded-xl shadow-lg max-h-80 overflow-y-auto">
            {groupedAccounts.length > 0 ? (
              groupedAccounts.map((group) => (
                <div key={group.type} className="p-2">
                  <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider border-b border-gray-100">
                    {group.type}s ({group.accounts.length})
                  </div>
                  {group.accounts.map((account) => (
                    <button
                      key={account.id}
                      onClick={() => {
                        setSelectedAccount(account);
                        setShowDropdown(false);
                        setSearchValue('');
                      }}
                      className="w-full p-3 hover:bg-gray-50 rounded-lg transition-colors duration-150 flex items-center space-x-3"
                    >
                      <div className={`p-2 rounded-lg ${getAccountTypeColor(account.type)}`}>
                        {getAccountIcon(account.type)}
                      </div>
                      <div className="flex-1 text-left">
                        <p className="font-medium text-gray-900">{account.name}</p>
                        <p className="text-sm text-gray-500">
                          {account.code} • {formatCurrency(account.balance)}
                        </p>
                      </div>
                    </button>
                  ))}
                </div>
              ))
            ) : (
              <div className="p-4 text-center text-gray-500">
                <Search className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                <p>No accounts found</p>
                <p className="text-xs">Try adjusting your search</p>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="max-w-7xl mx-auto p-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Fee Balances Dashboard</h1>
        </div>
        <button
          onClick={() => setShowTransferModal(true)}
          className="bg-black text-white px-6 py-3 rounded-xl font-medium hover:bg-gray-800 transition-colors duration-200 flex items-center space-x-2 shadow-sm"
        >
          <ArrowRightLeft className="w-5 h-5" />
          <span>Filter</span>
        </button>
      </div>

      {/* Account Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 lg:grid-cols-4 gap-4 mb-8 cursor-pointer" title="Click to view account transactions">
        {Object.entries(totals).map(([type, total]) => (
          <div
            key={type}
            onClick={() => handleAccountFilter(type)}
            className="bg-white p-4 rounded-xl border border-gray-100 shadow-sm"
          >
            <div className="flex items-center space-x-3">
              <div className={`p-2 rounded-lg ${getAccountTypeColor(type)}`}>
                {getAccountIcon(type)}
              </div>
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">{type}</p>
                <p className="text-lg font-bold text-gray-900">{formatCurrency(total)}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Navigation */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm mb-6">
        <div className="flex border-b border-gray-100">
          {[
            { id: 'transfer', label: 'Overall Balances' },
            { id: 'accounts', label: 'School Fee Balances' },
            { id: 'transport', label: 'Transport Fee Balances' },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors duration-200 ${
                activeTab === tab.id
                  ? 'border-black text-black'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Search + Export Bar */}
        <div className="p-6 border-b border-gray-100 flex flex-col sm:flex-row justify-between items-center gap-4">
          <div className="flex-1 relative max-w-md w-full">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => {
                if (activeTab === 'transfer') setSearchTerms(e.target.value);
                if (activeTab === 'accounts') setSearchTerm2(e.target.value);
                if (activeTab === 'transport') setSearchTerm3(e.target.value);
              }}
              placeholder="Search name or admission number..."
              className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent transition-all duration-200"
            />
          </div>

          <button
            onClick={() => {
              let data, fileName, sheetName;
              if (activeTab === 'transfer') {
                data = filteredOverallRecords;
                fileName = 'Overall_Fee_Balances';
                sheetName = 'Overall';
              } else if (activeTab === 'accounts') {
                data = filteredOverallFeeRecords;
                fileName = 'School_Fee_Balances';
                sheetName = 'School Fees';
              } else {
                data = filteredOverallTransportRecords;
                fileName = 'Transport_Fee_Balances';
                sheetName = 'Transport Fees';
              }
              exportToExcel(data, fileName, sheetName);
            }}
            className="bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-xl font-medium transition flex items-center gap-2 shadow-sm whitespace-nowrap"
          >
            <Download className="w-5 h-5" />
            Export to Excel
          </button>
        </div>

        {/* Content based on active tab */}
        {activeTab === 'transfer' && (
          <div>
            <div className="divide-y divide-gray-100">
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm text-left border">
                  <thead className="bg-gray-100 text-gray-700 uppercase">
                    <tr>
                      <th className="px-4 py-2 border">Admission</th>
                      <th className="px-4 py-2 border">Student</th>
                      <th className="px-4 py-2 border">Class</th>
                      <th className="px-4 py-2 border text-right">Total Fee</th>
                      <th className="px-4 py-2 border text-right">Total Paid</th>
                      <th className="px-4 py-2 border text-right">Total Balance</th>
                      <th className="px-4 py-2 border text-right">Total Excess</th>
                      <th className="px-4 py-2 border text-right"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredOverallRecords.map((entry, i) => (
                      <tr
                        key={i}
                        className="hover:bg-gray-50"
                        onClick={() => navigate(`/students/${entry.id}/viewinvoice`)}
                      >
                        <td className="px-4 py-2 border">{entry.admission}</td>
                        <td className="px-4 py-2 border">{entry.studentName}</td>
                         <td className="px-4 py-2 border">{entry.class}</td>
                        <td className="px-4 py-2 border text-right text-dark-600">{entry.amount.toLocaleString()}</td>
                        <td className="px-4 py-2 border text-right text-green-600">{entry.paid.toLocaleString()}</td>
                        <td className="px-4 py-2 border text-right text-red-600">{entry.balance.toLocaleString()}</td>
                        <td className="px-4 py-2 border text-right text-blue-600">{entry.excess.toLocaleString()}</td>
                        <td className="px-4 py-2 border text-right text-blue-600">
                          <button
                            onClick={() => navigate(`/students/${entry.id}/viewinvoice`)}
                            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center space-x-2 transition-colors"
                          >
                            <Calculator className="h-4 w-4" />
                            <span>View invoice</span>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'accounts' && (
          <div>
            <div className="divide-y divide-gray-100">
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm text-left border">
                  <thead className="bg-gray-100 text-gray-700 uppercase">
                    <tr>
                      <th className="px-4 py-2 border">Admission</th>
                      <th className="px-4 py-2 border">Student</th>
                       <th className="px-4 py-2 border">Class</th>
                      <th className="px-4 py-2 border text-right">Total Fee</th>
                      <th className="px-4 py-2 border text-right">Total Paid</th>
                      <th className="px-4 py-2 border text-right">Total Balance</th>
                      <th className="px-4 py-2 border text-right">Total Excess</th>
                      <th className="px-4 py-2 border text-right"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredOverallFeeRecords.map((entry, i) => (
                      <tr
                        key={i}
                        className="hover:bg-gray-50"
                        onClick={() => navigate(`/students/${entry.quotation_id}/viewinvoice`)}
                      >
                        <td className="px-4 py-2 border">{entry.admission}</td>
                        <td className="px-4 py-2 border">{entry.studentName}</td>
                        <td className="px-4 py-2 border">{entry.class}</td>
                        <td className="px-4 py-2 border text-right text-dark-600">{entry.amount.toLocaleString()}</td>
                        <td className="px-4 py-2 border text-right text-green-600">{entry.paid.toLocaleString()}</td>
                        <td className="px-4 py-2 border text-right text-red-600">{entry.balance.toLocaleString()}</td>
                        <td className="px-4 py-2 border text-right text-blue-600">{entry.excess.toLocaleString()}</td>
                        <td className="px-4 py-2 border text-right text-blue-600">
                          <button
                            onClick={() => navigate(`/students/${entry.quotation_id}/viewinvoice`)}
                            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center space-x-2 transition-colors"
                          >
                            <Calculator className="h-4 w-4" />
                            <span>View invoice</span>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'transport' && (
          <div>
            <div className="divide-y divide-gray-100">
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm text-left border">
                  <thead className="bg-gray-100 text-gray-700 uppercase">
                    <tr>
                      <th className="px-4 py-2 border">Admission</th>
                      <th className="px-4 py-2 border">Student</th>
                      <th className="px-4 py-2 border">Class</th>
                      <th className="px-4 py-2 border text-right">Total Fee</th>
                      <th className="px-4 py-2 border text-right">Total Paid</th>
                      <th className="px-4 py-2 border text-right">Total Balance</th>
                      <th className="px-4 py-2 border text-right">Total Excess</th>
                      <th className="px-4 py-2 border text-right"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredOverallTransportRecords.map((entry, i) => (
                      <tr
                        key={i}
                        className="hover:bg-gray-50"
                        onClick={() => navigate(`/students/${entry.quotation_id}/viewinvoice`)}
                      >
                        <td className="px-4 py-2 border">{entry.admission}</td>
                        <td className="px-4 py-2 border">{entry.studentName}</td>
                        <td className="px-4 py-2 border">{entry.class}</td>
                        <td className="px-4 py-2 border text-right text-dark-600">{entry.amount.toLocaleString()}</td>
                        <td className="px-4 py-2 border text-right text-green-600">{entry.paid.toLocaleString()}</td>
                        <td className="px-4 py-2 border text-right text-red-600">{entry.balance.toLocaleString()}</td>
                        <td className="px-4 py-2 border text-right text-blue-600">{entry.excess.toLocaleString()}</td>
                        <td className="px-4 py-2 border text-right text-blue-600">
                          <button
                            onClick={() => navigate(`/students/${entry.quotation_id}/viewinvoice`)}
                            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center space-x-2 transition-colors"
                          >
                            <Calculator className="h-4 w-4" />
                            <span>View invoice</span>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Enhanced Filter Modal */}
      {showTransferModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
            <div className="p-6 border-b border-gray-100">
              <h2 className="text-2xl font-bold text-gray-900">Filter Fee Balances</h2>
              <p className="text-gray-600 mt-1">Filter balances by grade, dates and term.</p>
            </div>

            <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Filter By Class</label>
                  <select
                    value={selectedClass}
                    onChange={(e) => setSelectedClass(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg p-2.5 focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none bg-gray-50"
                  >
                    <option value="">Select</option>
                    {classGrades.map((s) => (
                      <option key={s.value} value={s.value}>
                        {s.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Filter By Term</label>
                  <select
                    value={termId}
                    onChange={(e) => setTerm(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg p-2.5 focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none bg-gray-50"
                  >
                    <option value="0">Select Term</option>
                    {terms.map((term) => (
                      <option key={term.id} value={term.id}>
                        {term.term}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Select Start Date</label>
                  <div className="relative">
                    <input
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent transition-all duration-200 text-lg"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Select End Date</label>
                  <div className="relative">
                    <input
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent transition-all duration-200 text-lg"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="p-6 border-t border-gray-100 bg-gray-50">
              <div className="flex space-x-3">
                <button
                  onClick={() => {
                    setShowTransferModal(false);
                    setSelectedFromAccount(null);
                    setSelectedToAccount(null);
                    setFromAccountSearch('');
                    setToAccountSearch('');
                    setTransferAmount('');
                    setDescription('');
                    setReference('');
                  }}
                  className="flex-1 py-3 px-4 border border-gray-200 rounded-xl text-gray-700 font-medium hover:bg-gray-100 transition-colors duration-200"
                >
                  Cancel
                </button>
                <button
                  onClick={handleTransfer}
                  className="flex-1 py-3 px-4 bg-black text-white rounded-xl font-medium hover:bg-gray-800 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors duration-200 flex items-center justify-center space-x-2"
                >
                  <ArrowRightLeft className="w-4 h-4" />
                  <span>Filter</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AccountTransferManager;